import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { formatEUR } from "../lib/api";
import AuthDialog from "./AuthDialog";

const CartDrawer = () => {
  const { items, update, remove, total, open, setOpen, count } = useCart();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const nav = useNavigate();

  const checkout = () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setOpen(false);
    nav("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl text-[#032B44] flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Il tuo carrello
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16 text-slate-500" data-testid="empty-cart">
              <Snowflake />
              <p className="text-sm">Il carrello è vuoto.</p>
            </div>
          )}
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-3 p-3 border border-sky-100 rounded-xl" data-testid={`cart-item-${it.slug}`}>
              <img src={it.image_url} alt="" className="w-16 h-16 object-cover rounded-lg" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-medium text-sm text-[#032B44] truncate">{it.name}</h4>
                    <p className="text-xs text-slate-500">{it.unit}</p>
                  </div>
                  <button onClick={() => remove(it.product_id)} className="text-slate-400 hover:text-red-500" data-testid={`cart-remove-${it.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1 border border-sky-100 rounded-lg">
                    <button onClick={() => update(it.product_id, it.quantity - 1)} className="w-7 h-7 grid place-items-center hover:bg-sky-50 rounded-lg" data-testid={`cart-dec-${it.slug}`}><Minus className="w-3 h-3" /></button>
                    <span className="text-sm font-medium w-7 text-center" data-testid={`cart-qty-${it.slug}`}>{it.quantity}</span>
                    <button onClick={() => update(it.product_id, it.quantity + 1)} className="w-7 h-7 grid place-items-center hover:bg-sky-50 rounded-lg" data-testid={`cart-inc-${it.slug}`}><Plus className="w-3 h-3" /></button>
                  </div>
                  <span className="text-sm font-semibold text-[#032B44]">{formatEUR(it.unit_price * it.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {count > 0 && (
          <div className="border-t border-sky-100 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Totale</span>
              <span className="font-heading text-2xl font-semibold text-[#032B44]" data-testid="cart-total">{formatEUR(total)}</span>
            </div>
            <Button onClick={checkout} className="w-full h-12 bg-[#032B44] hover:bg-[#00A3FF] text-white rounded-lg" data-testid="checkout-button">
              {user ? "Vai al checkout" : "Accedi per pagare"}
            </Button>
          </div>
        )}
      </SheetContent>
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} initialTab="login" />
    </Sheet>
  );
};

const Snowflake = () => (
  <div className="mx-auto w-14 h-14 rounded-full bg-sky-50 grid place-items-center mb-3">
    <ShoppingBag className="w-6 h-6 text-sky-400" />
  </div>
);

export default CartDrawer;
